import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	Box,
	Button,
	Chip,
	Divider,
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Skeleton,
	Typography,
	useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useLocation, useNavigate } from 'react-router';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { useTelematicsFeature } from '@acorn-portal/shared/src/hooks/useTelematicsFeature';
import { clearTrips, fetchTrips } from '@acorn-portal/shared/src/store/telematicsTripsSlice';
import { TripSummaryDto } from '@acorn-portal/shared/src/services/telematicsDriverTripsApi';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import TripMiniMap from './trip-mini-map';
import TripScoreBadge from './trip-score-badge';
import { formatDistance, formatDuration, formatTripTime, getDateLabel, normalizeScore } from './trip-utils';

/**
 * Props for the RecentTripsCard component.
 */
export interface RecentTripsCardProps {
	/** Policy number used to fetch the customer's trips. */
	policyNumber: string;
	/** Whether to show score badges on each trip. Default: false (scores are hidden unless explicitly enabled). */
	showDriverScore?: boolean;
	/** How many trips to fetch and list. Default: 3 */
	maxTrips?: number;
}

const DEFAULT_MAX_TRIPS = 3;
const MAP_HEIGHT = 180;

interface RecentTripRowProps {
	trip: TripSummaryDto;
	isLatest: boolean;
	showDriverScore: boolean;
	onSelect: (tripId: string) => void;
}

/** One selectable trip row: car icon, "Today, 08:00am", duration • distance • destination, score, chevron. */
const RecentTripRow: React.FC<RecentTripRowProps> = ({ trip, isLatest, showDriverScore, onSelect }) => {
	const theme = useTheme();
	const startedAt = trip.startTimeUtc ? new Date(trip.startTimeUtc) : undefined;
	const whenLabel = startedAt ? `${getDateLabel(startedAt)}, ${formatTripTime(startedAt)}` : 'Trip';
	const detailParts = [formatDuration(trip.durationSeconds ?? 0), formatDistance(trip.distanceMiles ?? 0)];
	if (trip.endAddress) detailParts.push(trip.endAddress);

	return (
		<ListItemButton
			onClick={() => trip.tripId && onSelect(trip.tripId)}
			disabled={!trip.tripId}
			sx={{ borderRadius: 2, px: 1.5, py: 1.25, gap: 1.5 }}
		>
			<ListItemIcon sx={{ minWidth: 0 }}>
				<Box
					sx={{
						width: 40,
						height: 40,
						borderRadius: '50%',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						backgroundColor: alpha(theme.palette.primary.main, isLatest ? 0.16 : 0.08),
					}}
				>
					<DirectionsCarOutlinedIcon sx={{ color: theme.palette.primary.main }} />
				</Box>
			</ListItemIcon>

			<ListItemText
				primary={
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<Typography component='span' fontWeight={600}>
							{whenLabel}
						</Typography>
						{isLatest && (
							<Chip label='Latest' size='small' color='primary' variant='outlined' sx={{ fontWeight: 600 }} />
						)}
					</Box>
				}
				secondary={detailParts.join(' • ')}
				slotProps={{ secondary: { noWrap: true } }}
			/>

			{showDriverScore && trip.totalScore !== undefined && <TripScoreBadge score={normalizeScore(trip.totalScore)} />}
			<ChevronRightIcon sx={{ color: theme.palette.text.secondary }} />
		</ListItemButton>
	);
};

/**
 * Dashboard card showing the customer's most recent trips.
 *
 * Fetches the latest `maxTrips` trips for the policy through the shared
 * `telematicsTrips` slice, shows the latest trip's route on a mini map,
 * lists each trip as a selectable row, and links to the full trip list.
 * Follows the same feature-flag guard and clear-on-unmount pattern as
 * `DriverScoreCard`. Renders nothing when the telematics feature flag is off
 * or no policy number is known.
 *
 * @component
 * @example
 * ```tsx
 * <RecentTripsCard policyNumber={fullPolicy.policyNumber} showDriverScore={showDriverScore} />
 * ```
 */
const RecentTripsCard: React.FC<RecentTripsCardProps> = ({
	policyNumber,
	showDriverScore = false,
	maxTrips = DEFAULT_MAX_TRIPS,
}) => {
	const theme = useTheme();
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useAppDispatch();
	const isTelematicsFeatureEnabled = useTelematicsFeature();
	const { items, loading, error } = useAppSelector((state) => state.telematicsTrips);

	/** The policy a first fetch has been dispatched for. Until it matches, the card is pending. */
	const [requestedPolicyNumber, setRequestedPolicyNumber] = useState<string | null>(null);

	const canLoad = isTelematicsFeatureEnabled && Boolean(policyNumber);

	const loadTrips = useCallback(
		() => dispatch(fetchTrips({ policyReference: policyNumber, pageNumber: 1, pageSize: maxTrips })),
		[dispatch, policyNumber, maxTrips],
	);

	// Fetch on mount / when the policy or page size changes; abort and clear on the way out
	useEffect(() => {
		if (!canLoad) return;
		setRequestedPolicyNumber(policyNumber);
		const request = loadTrips();
		return () => {
			request.abort();
			dispatch(clearTrips());
		};
	}, [canLoad, policyNumber, loadTrips, dispatch]);

	const trips = useMemo(() => items.slice(0, maxTrips), [items, maxTrips]);
	const latestTrip = trips[0];
	const returnTo = location.pathname + location.search;

	const handleSelectTrip = (tripId: string) =>
		navigate(ROUTE_PATHS.telematicsTripDetails.replace(':tripId', tripId), { state: { from: returnTo } });
	const handleViewAll = () => navigate(ROUTE_PATHS.telematicsTrips, { state: { from: returnTo } });

	const isPending = loading || requestedPolicyNumber !== policyNumber;
	const isLoading = isPending && trips.length === 0;
	const hasError = !isPending && Boolean(error) && trips.length === 0;
	const isEmpty = !isPending && !error && trips.length === 0;

	if (!canLoad) return null;

	return (
		<Box>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
				<Typography variant='h5' component='h2' sx={{ fontWeight: 500, color: theme.palette.secondary.main }}>
					Recent trips
				</Typography>
			</Box>

			{isLoading && (
				<Box role='status' aria-label='Loading recent trips'>
					<Skeleton variant='rectangular' height={MAP_HEIGHT} sx={{ borderRadius: 2, mb: 2 }} />
					{Array.from({ length: maxTrips }, (_, index) => (
						<Skeleton key={index} variant='rounded' height={56} sx={{ mb: 1 }} />
					))}
				</Box>
			)}

			{hasError && (
				<Box sx={{ textAlign: 'center', py: 3 }}>
					<Typography color='text.secondary' sx={{ mb: 2 }}>
						We couldn&apos;t load your recent trips.
					</Typography>
					<Button variant='outlined' startIcon={<RefreshIcon />} onClick={loadTrips} sx={{ textTransform: 'none' }}>
						Try again
					</Button>
				</Box>
			)}

			{isEmpty && (
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						textAlign: 'center',
						py: 3,
						px: 2,
						backgroundColor: theme.palette.grey[50],
						borderRadius: 2,
						border: `1px dashed ${theme.palette.grey[300]}`,
					}}
				>
					<DirectionsCarOutlinedIcon sx={{ fontSize: 48, color: theme.palette.grey[400], mb: 1 }} />
					<Typography fontWeight={600} color='text.secondary'>
						No trips recorded yet
					</Typography>
					<Typography variant='body2' color='text.disabled'>
						Your recent journeys will appear here once you start driving.
					</Typography>
				</Box>
			)}

			{trips.length > 0 && (
				<>
					{latestTrip?.polyline && (
						<Box sx={{ borderRadius: 2, overflow: 'hidden', mb: 2 }}>
							<TripMiniMap polyline={latestTrip.polyline} height={MAP_HEIGHT} />
						</Box>
					)}

					<List disablePadding aria-label='Recent trips'>
						{trips.map((trip, index) => (
							<React.Fragment key={trip.tripId ?? index}>
								<RecentTripRow
									trip={trip}
									isLatest={index === 0}
									showDriverScore={showDriverScore}
									onSelect={handleSelectTrip}
								/>
								{index < trips.length - 1 && <Divider component='li' />}
							</React.Fragment>
						))}
					</List>

					<Button
						variant='outlined'
						fullWidth
						color='primary'
						onClick={handleViewAll}
						sx={{
							mt: 2,
							whiteSpace: 'nowrap',
							marginX: 'auto',
							p: 1,
							fontSize: 17,
							textTransform: 'capitalize',
							fontWeight: 600,
							border: `2px solid ${theme.palette.primary.main}`,
							'&:hover': {
								backgroundColor: 'transparent',
								border: `2px solid ${theme.palette.primary.main}`,
							},
							'&.MuiButton-outlined': {
								border: `2px solid ${theme.palette.primary.main}`,
							},
						}}
					>
						View all trips
					</Button>
				</>
			)}
		</Box>
	);
};

export default RecentTripsCard;
